'use client';

import { useEffect, useRef, useState } from 'react';
import { isAdPersonalizationRestricted, waitForConsentApi } from '@/src/lib/datagrail/consent';
import {
  getSrpAdDevice,
  getSrpAdSlotPath,
  SRP_AD_SIZES,
  SRP_AD_SLOTS,
  type SrpAdDevice,
  type SrpAdSlot,
} from './constants';
import { type GoogleTagPubAds, type GoogleTagSlot, queueGptCommand } from './gpt';
import type { SrpAdTargeting } from './targeting';

// Legacy won't refresh a slot more than once per 10s (`ads.ts` shouldUpdateAd).
// Matching it keeps a map drag from burning through requests faster than they
// can accrue the 1s in-view that makes an impression billable.
const MIN_REFRESH_INTERVAL_MS = 10_000;

function readAdDevice(): SrpAdDevice {
  // Measured the way legacy did (min of the two widths, so scrollbars don't count).
  return getSrpAdDevice(Math.min(window.innerWidth, document.documentElement.clientWidth) || window.innerWidth);
}

function applyPrivacySettings(pubads: GoogleTagPubAds): void {
  // Set both directions every time: `pubads()` is a page-global singleton, so a
  // restriction left over from an earlier mount would otherwise keep later
  // requests non-personalized after the user grants consent.
  const isRestricted = isAdPersonalizationRestricted();
  pubads.setRequestNonPersonalizedAds(isRestricted ? 1 : 0);
  pubads.setPrivacySettings({ restrictDataProcessing: isRestricted });
}

/**
 * Defines the given SRP GAM slots, then keeps them earning: a later search
 * re-requests them in place via `pubads().refresh` rather than tearing them
 * down, mirroring legacy's `defineAd`/`refreshAd` pair.
 *
 * Only pass slots whose containers render — GPT can't display into a div that
 * isn't there. A null `targeting` holds the *first* ad request back until the
 * key/values are ready; once slots are live a null no longer tears them down,
 * since adtags goes pending again on every viewport change.
 */
export function useSrpGoogleAds(
  activeSlots: ReadonlyArray<SrpAdSlot>,
  targeting: SrpAdTargeting | null,
  adListingType: string,
): void {
  // Effects key off the ids rather than the array, which is rebuilt each render.
  const activeSlotIds = activeSlots.map((slot) => slot.id).join(',');
  const targetingKey = targeting === null ? null : JSON.stringify(targeting);

  const targetingRef = useRef(targeting);
  targetingRef.current = targeting;

  // The slots GPT currently holds, and what was last asked for on them.
  const liveSlotsRef = useRef<Array<GoogleTagSlot>>([]);
  const lastRequestedTargetingRef = useRef<string | null>(null);
  const lastRequestAtRef = useRef(0);
  const hasTargeting = targeting !== null;

  // Legacy recomputes the placement on every refresh and force-destroys when it
  // changed, so a rotated tablet stops reporting against the phone unit.
  const [adDevice, setAdDevice] = useState<SrpAdDevice | null>(() =>
    typeof window === 'undefined' ? null : readAdDevice(),
  );
  useEffect(() => {
    // Same value short-circuits the re-render, so this stays cheap while dragging.
    const sync = () => setAdDevice(readAdDevice());
    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  // Definition and teardown are split across two effects on purpose. Targeting
  // gates only this one, so it dropping back to null — which adtags does on
  // every viewport change — can't tear a live slot down; that was what made a
  // map drag destroy the ad and restart its viewability clock.
  useEffect(() => {
    if (!activeSlotIds || !hasTargeting || adDevice === null) return;
    // Already serving this slot set; a targeting change refreshes it below.
    if (liveSlotsRef.current.length > 0) return;

    const slotsToDefine = SRP_AD_SLOTS.filter((slot) => activeSlotIds.split(',').includes(slot.id));
    let cancelled = false;
    const consentWait = new AbortController();
    const definedSlots: Array<GoogleTagSlot> = [];

    // Hold the first ad request until DataGrail can report consent, so a
    // non-consenting user never gets a personalized one. If DataGrail never
    // loads, `isAdPersonalizationRestricted` leaves the request personalized —
    // see the fail-open rationale there.
    void waitForConsentApi({ signal: consentWait.signal }).then(() => {
      if (cancelled) return;

      queueGptCommand((gpt) => {
        if (cancelled) return;

        const appliedTargeting = targetingRef.current ?? {};
        for (const { id, format } of slotsToDefine) {
          const slot = gpt.defineSlot(getSrpAdSlotPath(adDevice, format, adListingType), SRP_AD_SIZES, id);
          if (!slot) continue;
          for (const [key, value] of Object.entries(appliedTargeting)) slot.setTargeting(key, value);
          definedSlots.push(slot);
        }

        const pubads = gpt.pubads();
        for (const slot of definedSlots) slot.addService(pubads);

        applyPrivacySettings(pubads);

        pubads.enableSingleRequest();
        pubads.enableAsyncRendering();
        pubads.collapseEmptyDivs();
        pubads.set('page_url', window.location.href);

        gpt.enableServices();
        for (const { id } of slotsToDefine) gpt.display(id);

        liveSlotsRef.current = definedSlots;
        lastRequestedTargetingRef.current = JSON.stringify(appliedTargeting);
        lastRequestAtRef.current = Date.now();
      });
    });

    // Only abandons an in-flight definition — the teardown effect owns destroying.
    return () => {
      cancelled = true;
      consentWait.abort();
    };
  }, [activeSlotIds, adDevice, adListingType, hasTargeting]);

  // Destroys only when the ad unit path or the slot set itself changes. React
  // runs every cleanup before any effect, so this releases the container ids
  // before the effect above redefines against them.
  useEffect(() => {
    return () => {
      const slots = liveSlotsRef.current;
      liveSlotsRef.current = [];
      lastRequestedTargetingRef.current = null;
      if (slots.length === 0) return;
      // Destroy rather than clear: a slot left defined against a container id
      // blocks `defineSlot` from redefining it when the SRP remounts.
      queueGptCommand((gpt) => gpt.destroySlots(slots));
    };
  }, [activeSlotIds, adDevice, adListingType]);

  useEffect(() => {
    if (targetingKey === null || targetingKey === lastRequestedTargetingRef.current) return;
    // Nothing displayed yet — the define effect will pick up the latest targeting.
    if (liveSlotsRef.current.length === 0) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const requestRefresh = () => {
      const slots = liveSlotsRef.current;
      const appliedTargeting = targetingRef.current;
      if (cancelled || slots.length === 0 || !appliedTargeting) return;
      // A redefine may have landed while this waited out the interval; it
      // already requested with current targeting, so there's nothing to refresh.
      if (Date.now() - lastRequestAtRef.current < MIN_REFRESH_INTERVAL_MS) return;

      lastRequestedTargetingRef.current = JSON.stringify(appliedTargeting);
      lastRequestAtRef.current = Date.now();

      queueGptCommand((gpt) => {
        const pubads = gpt.pubads();
        for (const slot of slots) {
          slot.clearTargeting();
          for (const [key, value] of Object.entries(appliedTargeting)) slot.setTargeting(key, value);
        }

        applyPrivacySettings(pubads);
        pubads.set('page_url', window.location.href);
        pubads.refresh(slots);
      });
    };

    const sinceLastRequest = Date.now() - lastRequestAtRef.current;
    if (sinceLastRequest >= MIN_REFRESH_INTERVAL_MS) {
      requestRefresh();
    } else {
      // Trailing edge: refresh with whatever targeting is current once the
      // interval is up, so a search made during the cooldown isn't skipped.
      timer = setTimeout(requestRefresh, MIN_REFRESH_INTERVAL_MS - sinceLastRequest);
    }

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [targetingKey]);
}
